import React, { useMemo } from 'react';

// import { Profile, UpdateProfile } from '../../../interfaces';
import {
	BottomActionLayout,
	BottomActionLayoutProps,
} from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { Blurb } from '@/components/shared/Inputs/Blurb';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import { useUpdateMyTeamMutation } from '@/graphql';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useTeam } from '@/providers/TeamProvider';
import { Formik } from 'formik';
// import { S3Object } from 'models';
import * as Yup from 'yup';

import styles from './ChurchProfile.module.scss';
import { getCountriesList } from './getCountriesList';
import { getDenominationsList } from './getDenominations';

const tootlipProps = {
	tooltipHeading: 'How we use church data',
	tooltip:
		'Asaph helps you discover songs that fit your local context. By considering your location and denomination, we highlight the top-rated songs that resonate best in churches like yours.',
};

export interface ChurchProfileProps {
	onSave: () => void;
	onBack?: () => void;
	hasBack?: boolean;
	actionLabel?: string;
	bottomActionProps?: Partial<BottomActionLayoutProps>;
	leadingContent?: React.ReactElement;
	noInitialName?: boolean;
	readOnly?: boolean;
}

type FormSchema = {
	name: string;
	country?: string | null;
	city?: string | null;
	denomination?: string | null;
};

const formValidationSchema: Yup.SchemaOf<FormSchema> = Yup.object().shape({
	name: Yup.string().required('Please enter your church name').min(1),
	country: Yup.string().optional().nullable(),
	city: Yup.string().optional().nullable(),
	denomination: Yup.string().optional().nullable(),
	// spotifyId: Yup.string().required('Please enter a valid url'),
});

export const ChurchProfile: React.FC<ChurchProfileProps> = ({
	onSave,
	onBack,
	hasBack = false,
	actionLabel = 'Save',
	bottomActionProps,
	leadingContent,
	noInitialName = false,
	readOnly = false,
}) => {
	// return null;
	const { team, refetch } = useTeam();
	const { mutateAsync: updateMyTeam, isLoading: submitting } =
		useUpdateMyTeamMutation();
	const { handleError } = useErrorHandler();

	const countries = useMemo(() => {
		return getCountriesList().map((value) => ({
			label: value,
			value,
		}));
	}, []);

	const denominations = useMemo(() => {
		return getDenominationsList().map((value) => ({
			label: value,
			value,
		}));
	}, []);

	const initialValues: FormSchema = {
		name: noInitialName ? '' : team.name,
		country: team.country || null,
		city: team.city || null,
		denomination: team.denomination || null,
	};

	return (
		<Formik
			key="asaphSignUn"
			initialValues={initialValues}
			validationSchema={formValidationSchema}
			validateOnMount
			onSubmit={async (values: FormSchema) => {
				try {
					await updateMyTeam({
						input: {
							name: values.name.trim(),
							country: values.country?.trim() || null,
							city: values.city?.trim() || null,
							denomination: values.denomination?.trim() || null,
						},
					});
					await refetch();
					onSave();
				} catch (error) {
					handleError({
						errorCode: 'UpdateChurchProfileError',
						friendlyMessage: 'Failed to update church profile',
						error,
					});
					return;
				}
			}}
		>
			{({
				handleSubmit,
				handleChange,
				handleBlur,
				setFieldValue,
				values,
				errors,
				touched,
				isValid,
			}) => (
				<BottomActionLayout
					{...bottomActionProps}
					actionContent={readOnly ? null : (
						<>
							{hasBack ? (
								<AsaphButton
									color="grey"
									label="Back"
									onClick={() => onBack?.()}
									fullWidth
								/>
							) : null}
							<AsaphButton
								color="blue"
								label={actionLabel}
								disabled={!isValid}
								onClick={() => handleSubmit()}
								fullWidth
							/>
						</>
					)}
				>
					<form
						onSubmit={handleSubmit}
						id="asaphSignIn"
						className={styles['root']}
					>
						{leadingContent}
						<Label>Church name <span style={{ color: 'var(--px-color-error, #e53e3e)' }}>*</span></Label>
						<FormInput
							placeholder="Enter your church name"
							name="name"
							id="name"
							autoComplete="off"
							value={values.name}
							onChange={(e) => {
								handleChange(e);
							}}
							onBlur={handleBlur}
							error={!!(touched.name && errors.name)}
							disabled={readOnly}
						/>
						{touched.name && errors.name && <Blurb isError>{errors.name}</Blurb>}

						<div className="p-sm" />
						<Label {...tootlipProps}>Country</Label>
						<Select
							placeholder="Select country"
							items={countries}
							onChange={(value) => {
								setFieldValue('country', value);
							}}
							value={values.country || ''}
							disabled={readOnly}
						/>

						<div className="p-sm" />
						<Label {...tootlipProps}>City</Label>
						<FormInput
							placeholder="Enter your nearest city"
							name="city"
							id="city"
							autoComplete="off"
							value={values.city || ''}
							onChange={(e) => {
								handleChange(e);
							}}
							error={!!(touched.city && errors.city)}
							disabled={readOnly}
						/>
						{touched.city && errors.city && <Blurb isError>{errors.city}</Blurb>}

						<div className="p-sm" />
						<Label {...tootlipProps}>Denomination</Label>
						<Select
							placeholder="Select denomination"
							items={denominations}
							onChange={(value) => {
								setFieldValue('denomination', value);
							}}
							value={values.denomination || ''}
							disabled={readOnly}
						/>
					</form>
				</BottomActionLayout>
			)}
		</Formik>
	);
};

export default ChurchProfile;
